/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import BoundingBox from "App/utils/bounding_box";
import {mat4, vec3} from "gl-matrix";
import RenderObject from "App/geometry/objects/object";

export default
class AbstractGeometry {
    protected object: RenderObject;

    // default values
    protected properties: any = {};

    public positionsDesc: VertexDataDescriptor = null;
    public normalsDesc: VertexDataDescriptor = null;
    public indicesDesc: VertexDataDescriptor = null;

    public nonIndexedPositionsDesc: VertexDataDescriptor = null;

    public numFaces: number = 0;
    public numIndices: number = 0;
    public vertexCount: number = 0;

    constructor(aObject) {
        this.object = aObject;
    }

    public parse(aGeometryProperties: any) {
        // merge the data in the aBinaryGeometryProperties with the default values
        for (let property in aGeometryProperties) {
            this.properties[property] = aGeometryProperties[property];
        }
    }

    public getLoadDataQuery(): any {
        return {
            url: this.object.getModel().getBaseUrl() + this.properties.src,
            binSize: this.getBinaryDataSize()
        }
    }

    public getProperties(): any {
        return this.properties;
    }

    public isInitialized(): boolean {
        return this.positionsDesc && this.positionsDesc.data.length > 0;
    }

    public computeBBox(): BoundingBox {
        let point = vec3.create();
        let bbox = new BoundingBox();
        for (let i = 0; i < this.positionsDesc.data.length; i += 3) {
            vec3.set(point, this.positionsDesc.data[i + 0], this.positionsDesc.data[i + 1], this.positionsDesc.data[i + 2]);
            bbox.extendByPoint(point);
        }

        return bbox;
    }

    /***
     * Transform positions and normals by the aTransformation matrix.
     * @param aTransformation transformation matrix
     */
    public transform(aTransformation: mat4|Float32Array) {
        this.positionsDesc.data =
            AbstractGeometry.transformArray(
                <Float32Array>this.positionsDesc.data,
                this.positionsDesc.nofElements,
                aTransformation
            );

        if (this.normalsDesc) {
            let rotationMat = mat4.clone(aTransformation);
            rotationMat[3] = 0.0;
            rotationMat[7] = 0.0;
            rotationMat[11] = 0.0;
            rotationMat[12] = 0.0;
            rotationMat[13] = 0.0;
            rotationMat[14] = 0.0;
            rotationMat[15] = 1.0;
            this.normalsDesc.data = AbstractGeometry.transformArray(<Float32Array>this.normalsDesc.data, this.normalsDesc.nofElements, rotationMat);
        }

        // recompute bbox from the transformed vertices. Transformation of just the min and max simply does not work,
        // because the aabb can be bigger after transformation compared to the local non aabb.
        let aabb = this.computeBBox();
        this.object.getProperties().bboxMin = vec3.clone(aabb.getMin());
        this.object.getProperties().bboxMax = vec3.clone(aabb.getMax());
    }

    public generateNonIndexedVertices(aAttributeSize: number) {
        let nonIndexedPositionsArray = new Float32Array(this.numIndices * aAttributeSize); // * aPrimitiveSize == e.g. xyz

        let indexedVertexIndex = 0;
        for (let inIndex = 0, outIndex = 0; inIndex < this.numIndices; inIndex++, outIndex += aAttributeSize) {
            indexedVertexIndex = this.indicesDesc.data[inIndex] * aAttributeSize;
            for (let pi = 0; pi < aAttributeSize; pi++) {
                nonIndexedPositionsArray[outIndex + pi] = this.positionsDesc.data[indexedVertexIndex + pi];
            }
        }

        return nonIndexedPositionsArray;
    }

    public toBase64(): string {
        throw new Error("ERROR: Method toBase64 of AbstractGeometry in derived class must be implemented.");
    }

    public updatePositions(aPositions) {
        let positions = [];
        let i;

        for (i = 0; i < aPositions.length; i++) {
            positions.push(aPositions[i]);
        }

        if (!this.positionsDesc) {
            // create new positions vertex data descriptor
            this.positionsDesc = new VertexDataDescriptor(
                "positions",
                3,
                positions.length / 3,
                "FLOAT",
                4,
                0,
                new Float32Array(positions)
            );
        } else {
            // just update the positions, we expect that the size is the same, but the coords are changed
            this.positionsDesc.data = new Float32Array(positions);
            this.generateNonIndexedVertices(3);
        }

        let bbox = this.computeBBox();
        vec3.copy(this.object.getProperties().bboxMin, bbox.getMin());
        vec3.copy(this.object.getProperties().bboxMax, bbox.getMax());
    }

    /***
     * Returns the positions buffer as reference.
     *
     * @returns {*}
     */
    public getPositions(): Float32Array {
        if (!this.positionsDesc) {
            return new Float32Array([0, 0, 0]);
        }

        return <Float32Array>this.positionsDesc.data;
    }

    public clear() {
        this.numFaces = 0;
        this.numIndices = 0;
        this.vertexCount = 0;

        this.positionsDesc = null;
    }

    /**
     * Static method
     * @param {array} aArrayToTransform Array of floats to transform
     * @param {int} aAttributesCount Number of 3D pFoints in the array to transform
     * @param {mat4} aTransformationMat A mat4 transformation matrix used to transform the points in the input array
     * @returns {Float32Array} transformed array of 3D points
     * @private
     */
    public static transformArray(aArrayToTransform: mat4|Float32Array, aAttributesCount: number, aTransformationMat: mat4| Float32Array): Float32Array {
        let transformedArray = new Float32Array(aAttributesCount * 3);
        let transformedAttribute = vec3.create();

        let point = vec3.create();
        for (let i = 0; i < aArrayToTransform.length; i += 3) {
            vec3.transformMat4(
                transformedAttribute,
                vec3.set(point, aArrayToTransform[i + 0], aArrayToTransform[i + 1], aArrayToTransform[i + 2]),
                aTransformationMat
            );
            transformedArray[i + 0] = transformedAttribute[0];
            transformedArray[i + 1] = transformedAttribute[1];
            transformedArray[i + 2] = transformedAttribute[2];
        }

        return transformedArray;
    }

    public getVerticesCount(): number {
        if (this.isInitialized()) {
            return this.vertexCount;
        }

        return this.properties.vertexCount;
    };

    public getVerticesCountEstimation(): number {
        return this.properties.vertexCount;
    }

    public getIndicesCount(): number {
        if (this.isInitialized()) {
            return this.numIndices;
        }

        return this.properties.indexCount;
    }

    public getIndicesCountEstimation(): number {
        return this.properties.indexCount;
    }

    public getFacesCount(): number {
        return this.numFaces;
    }

    public getBinaryDataSize(): number {
        return this.properties.binSize;
    }

    public loadData(aBuffer: any) {
        throw new Error("ERROR: Method loadData of AbstractGeometry in derived class must be implemented.");
    }

    protected setDescriptors(aData: any) {
        throw new Error("ERROR: Method setDescriptors of AbstractGeometry in derived class must be implemented.");
    }

    public getJSONRepresentation(): any {
        throw new Error("ERROR: Method getJSONRepresentation of AbstractGeometry in derived class must be implemented.");
    }

    releaseGeometryData() {
    }
}
